import type { ErrorDefinition } from "../types";

export const signingErrors = {
	"SIGNING.VIEW_REQUIRED": {
		title: "Open the document first",
		description: "Wait for the PDF to load, then sign again.",
		steps: [
			"Tap Accept file if you have not already.",
			"Wait until the PDF appears (unlock your keys if needed).",
			"Tap Sign again.",
		],
		supportSlug: "signing-view-required",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "FORBIDDEN",
	},
	"SIGNING.ACK_REQUIRED": {
		title: "Accept the file first",
		description: "Tap Accept file on the sign page, then try again.",
		steps: [
			"Read the acceptance text on the sign page.",
			"Tap Accept file.",
			"Wait for the document to load, then sign when ready.",
		],
		supportSlug: "signing-ack-required",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "FORBIDDEN",
	},
	"SIGNING.VIEW_AFTER_ACK": {
		title: "Open the document after accepting",
		description: "View the PDF after you accept, then sign again.",
		steps: [
			"Tap Accept file if you have not already.",
			"Wait until the PDF finishes loading on the sign page.",
			"Tap Sign again.",
		],
		supportSlug: "signing-view-after-ack",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "FORBIDDEN",
	},
	"SIGNING.VIEW_BEFORE_SIGN": {
		title: "Open the document first",
		description: "Wait for the PDF to load, then sign again.",
		steps: [
			"Tap Accept file if you have not already.",
			"Wait until the PDF appears (unlock your keys if needed).",
			"Tap Sign again.",
		],
		supportSlug: "signing-view-before-sign",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "FORBIDDEN",
	},
	"SIGNING.NOT_REQUIRED": {
		title: "You're not a signer",
		description: "This document doesn't list you as a signer.",
		steps: [
			"Check that you used the correct invite link.",
			"Contact the sender if you expected to sign.",
		],
		supportSlug: "signing-not-required",
		audience: "user",
		severity: "warning",
		defaultOrpcCode: "FORBIDDEN",
	},
	"SIGNING.ALREADY_SIGNED": {
		title: "You already signed",
		description: "Your signature is already on this document.",
		steps: ["Download the completed copy from your dashboard if you need one."],
		supportSlug: "signing-already-signed",
		audience: "user",
		severity: "warning",
		defaultOrpcCode: "CONFLICT",
	},
	"SIGNING.EMAIL_REQUIRED": {
		title: "Add your email to sign",
		description: "Your account email must match this document.",
		steps: [
			"Open Profile in the sidebar (Account → Profile).",
			"Add or verify your primary email.",
			"Return to the sign page and tap Sign again.",
		],
		supportSlug: "signing-email-required",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.PLACEMENT_INCOMPLETE": {
		title: "Complete required fields",
		description: "Fill every required field before signing.",
		steps: [
			"Tap each required field on the PDF (labeled Req until done).",
			"When every required field shows Done, tap Sign.",
		],
		supportSlug: "signing-placement-incomplete",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.FIELD_COMPLETION_MISSING": {
		title: "Field value missing",
		description: "Tap the field again to apply your value.",
		steps: ["Tap the field again to apply your signature or value."],
		supportSlug: "signing-field-completion-missing",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.FIELD_VISUAL_REQUIRED": {
		title: "Signature image required",
		description: "Apply your signature or initials to each field.",
		steps: [
			"Create a signature in your library if you have not already.",
			"Tap each signature field to apply your default.",
		],
		supportSlug: "signing-field-visual-required",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.FIELD_AUTO_REQUIRED": {
		title: "Fill date, name, or email fields",
		description: "Tap each auto-fill field before signing.",
		steps: ["Tap each auto-fill field to apply the value."],
		supportSlug: "signing-field-auto-required",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.FIELD_TEXT_REQUIRED": {
		title: "Text field required",
		description: "Enter text for each text field before signing.",
		steps: ["Tap text fields and enter the requested information."],
		supportSlug: "signing-field-text-required",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.FIELD_CHECKBOX_REQUIRED": {
		title: "Checkbox required",
		description: "Check each required checkbox before signing.",
		steps: ["Tap checkbox fields to mark them."],
		supportSlug: "signing-field-checkbox-required",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.SIGNATURE_INVALID": {
		title: "Signature couldn't be verified",
		description: "Sign again to continue.",
		steps: [
			"Return to the document and tap Sign again.",
			"If this keeps happening, refresh the page and retry.",
		],
		supportSlug: "signing-signature-invalid",
		audience: "user",
		severity: "error",
		defaultOrpcCode: "BAD_REQUEST",
	},
	"SIGNING.ROUTING_ORDER": {
		title: "Not your turn yet",
		description: "Wait for earlier signers to finish first.",
		steps: [
			"Check the Signing progress section on the sign page.",
			"Return and tap Sign after earlier signers finish.",
		],
		supportSlug: "signing-routing-order",
		audience: "user",
		severity: "warning",
		defaultOrpcCode: "FORBIDDEN",
	},
	"SIGNING.REPLACEMENT_PENDING": {
		title: "Signer change pending",
		description: "Finish or cancel the signer change before signing.",
		steps: [
			"Check pending changes in document settings.",
			"Wait for the change to be applied or cancelled.",
		],
		audience: "user",
		severity: "warning",
		defaultOrpcCode: "FORBIDDEN",
	},
} as const satisfies Record<string, ErrorDefinition>;
